<template>
    <div class="p-grid panel-demo">
        <div class="p-col-12 p-md-12">
            <div class="flexed-container card p-fluid">
                <h5>Welcome to {{ channelData?.data?.data?.subnetwork_name && channelData?.data?.data?.subnetwork_name.length > 0 ? channelData?.data?.data?.subnetwork_name : 'Channel' }} Home!</h5>
                <Button v-if="isAdmin && !defaultChannel" class="delete-channel-button" @click="confirmDelete">
                   Delete Channel
                </Button>
            </div>
        </div>

        <div class="p-col-12 p-md-12">
            <div class="card p-fluid">
                <div><img id="logo" :src="channelData?.data?.data?.subnetwork_logo?.attachments?.length > 0
                    ? channelData.data.data.subnetwork_logo.attachments[0]
                    : 'assets/layout/images/arenacx_teal_logo.png'" alt="logo" style="height: 12.25rem" /></div>
            </div>
        </div>
    </div>

    <div class="p-grid">
        <div class="p-col-12 p-md-12">

            <div class="card p-fluid">
                <div class="flexed-container">
                    <h4 style="width: 200px">RFP Insights</h4>
                    <p>Please note that the collection of metrics for RFP Insights is enabled on all RFPs created on or after April 10th, 2025.  We are currently investigating adding Insights to RFPs created before that date.</p>
                </div>

            </div>
            <div class="card p-fluid">
                <!-- New timeframe selector -->
                <div class="timeframe-selector mb-3">
                    <h5>Select Timeframe</h5>
                    <div class="timeframe-buttons">
                        <div class="button-group">
                            <h6>Previous</h6>
                            <Button 
                                :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-week' }"
                                @click="setTimeframe('prev-week')"
                            >Week</Button>
                            <Button 
                                :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-month' }"
                                @click="setTimeframe('prev-month')"
                            >Month</Button>
                            <Button 
                                :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-quarter' }"
                                @click="setTimeframe('prev-quarter')"
                            >Quarter</Button>
                        </div>
                        <div class="button-group">
                            <h6>Current</h6>
                            <Button 
                                :class="{ 'p-button-outlined': selectedTimeframe !== 'current-week' }"
                                @click="setTimeframe('current-week')"
                            >Week</Button>
                            <Button 
                                :class="{ 'p-button-outlined': selectedTimeframe !== 'current-month' }"
                                @click="setTimeframe('current-month')"
                            >Month</Button>
                            <Button 
                                :class="{ 'p-button-outlined': selectedTimeframe !== 'current-quarter' }"
                                @click="setTimeframe('current-quarter')"
                            >Quarter</Button>
                        </div>
                    </div>
                </div>

                <!-- Existing calendar (now optional) -->
                <div class="calender-wrapper">
                    <div class="calendar-container">
                        <Calendar v-model="dates" selectionMode="range" :manualInput="false"
                            :date-format="dd / MM / yyyy" showButtonBar />
                    </div>
                    <Button :disabled="!dates || dates?.length == 0 || dates[0] == null || dates[1] == null"
                        @click="getRfpListInsights" :loading="loading">Get Data</Button>
                </div>

                <div v-if="loading" class="loader">
                    <p>Loading...</p>
                </div>

                <template v-else>
                    <!-- Summary Table -->
                    <DataTable :value="summaryData" showGridlines class="mb-4" responsiveLayout="scroll">
                        <Column header="In Progress" field="inProgress">
                            <template #body="{ data }">
                                <template v-if="data.inProgress > 0">
                                    <a href="#in-progress" @click.prevent="scrollToSection('in-progress')" class="summary-link">{{ data.inProgress }}</a>
                                </template>
                                <template v-else>
                                    {{ data.inProgress }}
                                </template>
                            </template>
                        </Column>
                        <Column header="Awaiting Approval" field="awaitingApproval">
                            <template #body="{ data }">
                                <template v-if="data.awaitingApproval > 0">
                                    <a href="#awaiting-approval" @click.prevent="scrollToSection('awaiting-approval')" class="summary-link">{{ data.awaitingApproval }}</a>
                                </template>
                                <template v-else>
                                    {{ data.awaitingApproval }}
                                </template>
                            </template>
                        </Column>
                        <Column header="Out for Proposal" field="outForProposal">
                            <template #body="{ data }">
                                <template v-if="data.outForProposal > 0">
                                    <a href="#out-for-proposal" @click.prevent="scrollToSection('out-for-proposal')" class="summary-link">{{ data.outForProposal }}</a>
                                </template>
                                <template v-else>
                                    {{ data.outForProposal }}
                                </template>
                            </template>
                        </Column>
                        <Column header="Awaiting Scoring" field="awaitingScoring">
                            <template #body="{ data }">
                                <template v-if="data.awaitingScoring > 0">
                                    <a href="#awaiting-scoring" @click.prevent="scrollToSection('awaiting-scoring')" class="summary-link">{{ data.awaitingScoring }}</a>
                                </template>
                                <template v-else>
                                    {{ data.awaitingScoring }}
                                </template>
                            </template>
                        </Column>
                        <Column header="In Contracting" field="inContracting">
                            <template #body="{ data }">
                                <template v-if="data.inContracting > 0">
                                    <a href="#in-contracting" @click.prevent="scrollToSection('in-contracting')" class="summary-link">{{ data.inContracting }}</a>
                                </template>
                                <template v-else>
                                    {{ data.inContracting }}
                                </template>
                            </template>
                        </Column>
                        <Column header="Closed/Won" field="closedWon">
                            <template #body="{ data }">
                                <template v-if="data.closedWon > 0">
                                    <a href="#closed-won" @click.prevent="scrollToSection('closed-won')" class="summary-link">{{ data.closedWon }}</a>
                                </template>
                                <template v-else>
                                    {{ data.closedWon }}
                                </template>
                            </template>
                        </Column>
                        <Column header="Closed/Lost" field="closedLost">
                            <template #body="{ data }">
                                <template v-if="data.closedLost > 0">
                                    <a href="#closed-lost" @click.prevent="scrollToSection('closed-lost')" class="summary-link">{{ data.closedLost }}</a>
                                </template>
                                <template v-else>
                                    {{ data.closedLost }}
                                </template>
                            </template>
                        </Column>
                    </DataTable>

                    <!-- Separate tables for each status -->
                    <template v-for="(items, status) in groupedRfps" :key="status">
                        <div v-if="items.length > 0" class="status-section mb-4" :id="getSectionId(status)">
                            <h5>{{ status }}</h5>
                            <DataTable :value="items" showGridlines :scrollable="true" responsiveLayout="scroll" stripedRows
                                :resizableColumns="true">
                                <template #empty>
                                    <div class="empty-state">
                                        <i class="pi pi-info-circle text-gray-500 text-xl mb-2"></i>
                                        <p class="m-0">No records found for this status</p>
                                    </div>
                                </template>

                                <Column field="id" header="RFP ID" sortable>
                                    <template #body="{ data }">
                                        <div @click="navigateToRfpDetails(data.id)" class="opportunityLink">
                                            {{ data.id }}
                                        </div>
                                    </template>
                                </Column>
                                <Column field="name" header="Name of RFP" sortable class="wrap-column">
                                    <template #body="{ data }">
                                        <div class="wrap-text">{{ data.name }}</div>
                                    </template>
                                </Column>
                                <Column field="companyName" header="Company Name" sortable></Column>
                                <Column field="owner" header="Owner" sortable></Column>
                                <Column field="projectType" header="Project Type" sortable></Column>
                                <Column field="published" header="Published" sortable></Column>
                                <Column field="closeDate" header="Close Date" sortable></Column>
                            </DataTable>
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </div>

    <!-- Add Dialog component -->
    <Dialog v-model:visible="deleteDialog" modal header="Confirm Deletion" :style="{ width: '350px' }">
        <div class="confirmation-content">
            <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--yellow-700)" />
            <span>Are you sure you want to delete this channel?</span>
        </div>
        <template #footer>
            <Button label="No" icon="pi pi-times" @click="deleteDialog = false" class="p-button-text"/>
            <Button label="Yes" icon="pi pi-check" @click="deleteChannel" class="p-button-danger" />
        </template>
    </Dialog>
</template>

<script>

import CurrentUserService from './../service/CurrentUserService';
import RfpInsightsService from './../service/RfpInsightsService';
import ChannelService from './../service/ChannelService';
import { getAccount } from '../service/common';
export default {
    data() {
        return {
            products: null,
            dates: null,
            listPayload: null,
            userData: null,
            channelData: null,
            deleteDialog: false,
            isAdmin: false,
            subnetworkId: null,
            accountId: null,
            summaryData: [],
            groupedRfps: {},
            selectedTimeframe: 'prev-month', // Default timeframe
            loading: false,
            defaultChannel: false,
            DEFAULT_CHANNEL_SUBNETWORK_ID: "f4cd7264-3f00-463b-b2e9-d9e69c3ec193",
        };
    },


    productService: null,
    rfpInsightsService: null,
    userService: null,
    channelService: null,

    async created() {
        this.userService = new CurrentUserService();
        this.rfpInsightsService = new RfpInsightsService();
        this.channelService = new ChannelService();
        this.userData = this.userService.marketplaceUser;
        this.isAdmin = getAccount();
        this.subnetworkId = localStorage.getItem('subnetworkId');
        this.accountId = localStorage.getItem('accountId');
        this.channelData = await this.channelService.getChannelData(this.isAdmin ? this.subnetworkId : this.userData.subnetworkId);
        this.setTimeframe(this.selectedTimeframe); // Set initial timeframe
        this.defaultChannel = this.subnetworkId === this.DEFAULT_CHANNEL_SUBNETWORK_ID;
    },

    methods: {
        setTimeframe(timeframe) {
            this.selectedTimeframe = timeframe;
            const today = new Date();
            let start = new Date();
            let end = new Date();

            switch (timeframe) {
                case 'prev-week':
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
                    break;
                case 'prev-month':
                    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    end = new Date(today.getFullYear(), today.getMonth(), 0);
                    break;
                case 'prev-quarter': {
                    const quarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), (quarter - 1) * 3, 1);
                    end = new Date(today.getFullYear(), quarter * 3, 0);
                    break;
                }
                case 'current-week': {
                    const day = today.getDay();
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - day);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (6 - day));
                    break;
                }
                case 'current-month':
                    start = new Date(today.getFullYear(), today.getMonth(), 1);
                    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    break;
                case 'current-quarter': {
                    const currentQuarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), currentQuarter * 3, 1);
                    end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
                    break;
                }
            }

            this.dates = [start, end];
            this.getRfpListInsights();
        },

        // Navigate to RFP details page
        navigateToRfpDetails(id) {
            if (process.env.VUE_APP_ENV_TYPE == "DEV") {
                window.open(`${process.env.VUE_APP_RFPERFECT_FRONTEND_URL}/details?id=${id}`, '_blank');
            } else if (process.env.VUE_APP_ENV_TYPE == "UAT") {
                window.open(`${process.env.VUE_APP_RFPERFECT_FRONTEND_URL}/details?id=${id}`, '_blank');
            } else if (process.env.VUE_APP_ENV_TYPE == "PROD") {
                window.open(`${process.env.VUE_APP_RFPERFECT_FRONTEND_URL}/details?id=${id}`, '_blank');
            }
        },

        // Get RFP list insights
        async getRfpListInsights() {
            try {
                this.loading = true;
                this.listPayload = {
                    startDate: new Date(this.dates[0].getTime() - this.dates[0].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T00:00:00.000Z',
                    endDate: new Date(this.dates[1].getTime() - this.dates[1].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T23:59:59.999Z',
                    channelId: this.isAdmin ? this.subnetworkId : this.userData.subnetworkId,
                }
                const res = await this.rfpInsightsService.getRfpListInsights(this.listPayload);
                
                // Process data for summary table
                const statusCounts = {
                    inProgress: 0,
                    awaitingApproval: 0,
                    outForProposal: 0,
                    awaitingScoring: 0,
                    inContracting: 0,
                    closedWon: 0,
                    closedLost: 0
                };

                // Initialize grouped RFPs
                this.groupedRfps = {
                    'In Progress': [],
                    'Awaiting Approval': [],
                    'Out for Proposal': [],
                    'Awaiting Scoring': [],
                    'In Contracting': [],
                    'Closed/Won': [],
                    'Closed/Lost': []
                };

                // Process each RFP
                res.data.forEach(item => {
                    const processedItem = {
                        id: item.id,
                        name: item.name,
                        companyName: item.company_name ? item.company_name : 'N/A',
                        owner: item.first_name + ' ' + item.last_name, 
                        projectType: item.tags?.directory || 'N/A',
                        published:  item.published_at ? new Date(item.published_at).toLocaleDateString() : 'N/A',
                        closeDate:item.end_date ? new Date(item.end_date).toLocaleDateString() : 'N/A'
                    };
                    console.log("ITEM", item);
                    // Map status_display_name to categories and group RFPs
                    switch(item.status_display_name) {
                        case 'Create Your RFP':
                            statusCounts.inProgress++;
                            this.groupedRfps['In Progress'].push(processedItem);
                            break;
                        case 'Validate and Publish':
                            statusCounts.awaitingApproval++;
                            this.groupedRfps['Awaiting Approval'].push(processedItem);
                            break;
                        case 'Receive Vendor Responses':
                            statusCounts.outForProposal++;
                            this.groupedRfps['Out for Proposal'].push(processedItem);
                            break;
                        case 'Pick Your Winner':
                            statusCounts.awaitingScoring++;
                            this.groupedRfps['Awaiting Scoring'].push(processedItem);
                            break;
                        case 'Execute Your Contract':
                            statusCounts.inContracting++;
                            this.groupedRfps['In Contracting'].push(processedItem);
                            break;
                        case 'Rejected':
                            statusCounts.closedLost++;
                            this.groupedRfps['Closed/Lost'].push(processedItem);
                            break;
                        case 'Completed':
                            statusCounts.closedWon++;
                            this.groupedRfps['Closed/Won'].push(processedItem);
                            break;
                        // Add more cases as needed
                    }
                });

                this.summaryData = [statusCounts];
            } catch (error) {
                console.error('Error fetching RFP insights:', error);
            } finally {
                this.loading = false;
            }
        },

        confirmDelete() {
            this.deleteDialog = true;
        },

        async deleteChannel() {
            try {
                const res = await this.channelService.deleteChannel(this.accountId);
                console.log('DELETE CHANNEL RESPONSE >>', res);
                if (res.status === 200 || res.status === 204) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Channel deleted successfully',
                        life: 3000
                    });
                    
                    this.deleteDialog = false;
                    
                    // Redirect to homepage after a short delay
                        this.$router.push('/');
                
                } else {
                    throw new Error('Failed to delete channel');
                }
            } catch (error) {
                console.error('Error deleting channel:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete channel. Please try again.',
                    life: 3000
                });
                this.deleteDialog = false;
            }
        },

        getSectionId(status) {
            // Convert status to lowercase and replace spaces and slashes with hyphens
            return status.toLowerCase().replace(/[\s/]+/g, '-');
        },

        scrollToSection(sectionId) {
            console.log('Scrolling to section:', sectionId);
            const element = document.getElementById(sectionId);
            console.log('Found element:', element);
            if (element) {
                const offset = 80; // Add 100px margin top
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.para {
    line-height: 1.5;
    margin: 0;
}

.opportunityLink {
    color: darkgreen;
    text-decoration: underline;
    cursor: pointer;
}

.calender-wrapper {
    display: flex;
    margin-top: 10px;
    margin-bottom: 16px;
    gap: 10px;
}

.calendar-container {
    width: 200px;
}

.empty-state {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
}

.flexed-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.delete-channel-button {
   width: 120px !important;
   text-align: center;
   justify-content: center;
}

.delete-channel-button:hover {
    background-color: red;
    color: white;
}

.confirmation-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
}

.status-section {
    margin-top: 2rem;
    
    h5 {
        margin-bottom: 1rem;
    }
}

.timeframe-selector {
    .timeframe-buttons {
        display: flex;
        gap: 2rem;
        
        .button-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            h6 {
                margin: 0;
                min-width: 70px;
            }

            button {
                min-width: 100px;
            }
        }
    }
}

.p-button:enabled:focus{
    background: #00796b !important;
}

// Optional: Make the calendar section collapsible or less prominent
.calender-wrapper {
    margin-top: 2rem;
    opacity: 0.8;
}

.wrap-column {
    max-width: 300px; // Adjust this value based on your needs
}

.wrap-text {
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
}

.loader {
    display: flex;
    justify-content: center;
    margin: 2rem auto;
}

.flexed-container-rfp-insights {
    display: flex;
    align-items: center;
}

// Add these new styles
:deep(.p-datatable) {
    .p-datatable-header {
        background-color: #e4edd9 !important;
        font-size: 1.1rem;
        font-weight: bold;
    }

    .p-datatable-thead > tr > th {
        background-color: #e4edd9 !important;
        color: #333;
        font-size: 1.1rem;
        font-weight: bold;
        padding: 1rem;
        border: 1px solid #ddd;
    }

    .p-datatable-tbody > tr > td {
        padding: 0.75rem 1rem;
        border: 1px solid #ddd;
    }

    // Add subtle hover effect on rows
    .p-datatable-tbody > tr:hover {
        background-color: #f8f9fa;
    }

    // Add subtle shadow to the table
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.summary-link {
    color: #00796b;
    text-decoration: underline;
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #004d40;
        text-decoration: underline;
    }
}

</style>
